import styled, { css, keyframes } from "styled-components";

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const confetti = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

export const Wrapper = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  background: var(--white);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: ${slideIn} 0.3s ease-out;
  z-index: 1000;

  @media (max-width: 480px) {
    width: calc(100% - 40px);
    left: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: linear-gradient(135deg, #41b32d 0%, #278f15 100%);
  color: var(--white);
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.95;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const RocketIcon = styled.span`
  font-size: 20px;
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ProgressBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

export const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.4s ease;
  transform: ${({ $isExpanded }) =>
    $isExpanded ? "rotate(0)" : "rotate(-180deg)"};

  svg path {
    fill: var(--white);
  }
`;

export const Content = styled.div`
  max-height: ${({ $isExpanded }) => ($isExpanded ? "600px" : "0")};
  overflow-y: auto;
  overflow-x: hidden;
  transition: max-height 0.6s ease;
`;

export const ProgressBar = styled.div`
  height: 4px;
  background: var(--gray-100, #f0f0f0);

  > div {
    height: 100%;
    background: var(--success-medium, #22c55e);
    transition: width 0.5s ease;
    width: ${({ $progress }) => $progress}%;
  }
`;

export const TaskList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 0;
`;

export const TaskItemWrapper = styled.li`
  border-bottom: 1px solid var(--gray-100, #f0f0f0);

  &:last-child {
    border-bottom: none;
  }
`;

export const TaskItemHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--gray-50, #fafafa);
  }

  ${({ $completed }) =>
    $completed &&
    css`
      opacity: 0.6;
    `}
`;

export const TaskItemContent = styled.div`
  max-height: ${({ $isExpanded }) => ($isExpanded ? "300px" : "0")};
  overflow: hidden;
  transition: max-height 0.6s ease;
`;

export const TaskGifPlaceholder = styled.div`
  margin: 0 16px 12px;
  padding: 12px;
  background: var(--gray-100, #eef0f3);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  img,
  video {
    width: 100%;
    border-radius: 6px;
  }
`;

export const PlaceholderBox = styled.div`
  width: 100%;
  height: 120px;
  background: linear-gradient(
    135deg,
    var(--gray-200, #e5e7eb) 0%,
    var(--gray-300, #d1d5db) 100%
  );
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-500, #6b7280);
  font-size: 12px;
`;

export const MarkDoneButton = styled.button`
  background: var(--brand-ideolo);
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid
    ${({ $completed }) =>
      $completed
        ? "var(--success-medium, #22c55e)"
        : "var(--gray-300, #d1d5db)"};
  background: ${({ $completed }) =>
    $completed ? "var(--success-medium, #22c55e)" : "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  ${({ $completed }) =>
    $completed &&
    css`
      animation: ${confetti} 0.3s ease;
    `}

  svg {
    width: 12px;
    height: 12px;
  }

  svg path {
    fill: white;
  }
`;

export const TaskContent = styled.div`
  flex: 1;
`;

export const TaskTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-900, #111827);
  text-decoration: ${({ $completed }) =>
    $completed ? "line-through" : "none"};
`;

export const Footer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid var(--gray-100, #f0f0f0);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const DismissButton = styled.button`
  background: none;
  border: none;
  color: var(--gray-500, #6b7280);
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--gray-100, #f0f0f0);
    color: var(--gray-700, #374151);
  }
`;

export const TaskDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--gray-600, #4b5563);
  line-height: 1.5;
  width: 100%;
`;

export const LearnMoreLink = styled.a`
  display: inline-block;
  margin-left: 4px;
  font-size: 12px;
  color: var(--brand-ideolo);
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const CompletedMessage = styled.div`
  padding: 20px 16px;
  text-align: center;

  h4 {
    margin: 0 0 8px;
    font-size: 16px;
    color: var(--gray-900, #111827);
  }

  p {
    margin: 0;
    font-size: 13px;
    color: var(--gray-500, #6b7280);
  }
`;
